/**
 * Row density, comfortable or compact (DesignSystem 6.1: the filter bar's density toggle).
 * Stored per browser under the same key and <html data-density> attribute the token layer
 * reads, so CSS and components agree.
 */
import { create } from 'zustand';

export type Density = 'comfortable' | 'compact';
export const DENSITY_KEY = 'fd.density';

/** Row heights in px for tables and lists, per density. */
export const ROW_HEIGHT: Record<Density, number> = { comfortable: 40, compact: 32 };
/** Two-line list rows (a title and a detail line). */
export const ROW_HEIGHT_TALL: Record<Density, number> = { comfortable: 52, compact: 40 };

export function isDensity(v: unknown): v is Density {
  return v === 'comfortable' || v === 'compact';
}

function read(): Density {
  try {
    const v = typeof localStorage === 'undefined' ? null : localStorage.getItem(DENSITY_KEY);
    return isDensity(v) ? v : 'comfortable';
  } catch {
    return 'comfortable';
  }
}

function apply(d: Density): void {
  if (typeof document !== 'undefined') document.documentElement.dataset.density = d;
}

interface DensityStore {
  density: Density;
  setDensity: (d: Density) => void;
  toggleDensity: () => void;
}

const initial = read();
apply(initial);

export const useDensityStore = create<DensityStore>()((set, get) => ({
  density: initial,
  setDensity: (density) => {
    try {
      localStorage.setItem(DENSITY_KEY, density);
    } catch {
      // private mode: lasts for this tab only
    }
    apply(density);
    set({ density });
  },
  toggleDensity: () => get().setDensity(get().density === 'compact' ? 'comfortable' : 'compact'),
}));

export const useDensity = (): Density => useDensityStore((s) => s.density);
export const setDensity = (d: Density): void => useDensityStore.getState().setDensity(d);
export const toggleDensity = (): void => useDensityStore.getState().toggleDensity();
